/**
 * /admin/customers/:tenantId — Per-tenant drilldown.
 * Tabs: Overview · Conversations · Invoices · Members · Ledger.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Building2, User, Download, Wallet, Activity, MapPin,
  MessageSquare, ThumbsUp, ThumbsDown, FileText, Receipt, Users as UsersIcon,
  Calendar, Globe2, ExternalLink,
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';
import { adminCustomersApi } from '../../services/api';
import { toast } from 'sonner';

const fmtMoney = (v, cur = 'INR') => {
  if (v == null) return '—';
  const sym = cur === 'INR' ? '₹' : '$';
  return `${sym}${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};
const fmtNum = (v) => (v == null ? '—' : Number(v).toLocaleString());
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString() : '—');

const MODEL_COLORS = {
  'claude-opus-4-6': '#6BA6F5',
  'claude-opus-4-7': '#F26B1F',
  'claude-opus-4-8': '#D946EF',
  'claude-opus-4-6':   '#6BA6F5',
  'claude-opus-4-7':   '#F26B1F',
  unknown:             '#718096',
};

const Tile = ({ label, value, sub, icon: Icon, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
    className="rounded-xl p-4"
    style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}
  >
    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}22`, color }}>
      <Icon size={14} />
    </div>
    <div className="mt-3 text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>{label}</div>
    <div className="mt-1 font-heading text-2xl font-semibold text-nexus-text-primary">{value}</div>
    {sub && <div className="text-[11px] text-nexus-text-tertiary mt-0.5">{sub}</div>}
  </motion.div>
);

const TABS = ['overview', 'conversations', 'invoices', 'members', 'ledger'];

const AdminCustomerDetail = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [data, setData] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);

  useEffect(() => {
    adminCustomersApi.detail(tenantId, 90)
      .then(({ data }) => setData(data))
      .catch(() => toast.error('Failed to load tenant'));
  }, [tenantId]);

  useEffect(() => {
    if (tab === 'conversations' && conversations.length === 0) {
      adminCustomersApi.conversations(tenantId, { limit: 50 })
        .then(({ data }) => setConversations(data.items))
        .catch(() => toast.error('Failed to load conversations'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const openConv = async (c) => {
    try {
      const { data } = await adminCustomersApi.conversation(tenantId, c.id);
      setActiveConv(data);
    } catch { toast.error('Could not load conversation'); }
  };

  const downloadInvoice = async (inv) => {
    try {
      const { data: blob } = await adminCustomersApi.invoicePdf(tenantId, inv.id);
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  if (!data) return <div className="text-nexus-text-tertiary">Loading…</div>;

  const t = data.tenant;
  const isPersonal = t.account_type === 'personal';
  const pool = t.pool_wallet || {};
  const geo = t.signup_geo || {};
  const revInr = data.revenue_by_currency?.find(r => r._id === 'INR');
  const revUsd = data.revenue_by_currency?.find(r => r._id === 'USD');
  const totalCredits = (data.timeseries || []).reduce((s, d) => s + (d.credits || 0), 0);
  const totalCalls = (data.timeseries || []).reduce((s, d) => s + (d.calls || 0), 0);
  const totalTokens = (data.timeseries || []).reduce((s, d) => s + (d.tokens || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-customer-detail">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <button onClick={() => navigate('/admin/customers')}
            className="p-1.5 rounded-lg hover:bg-white/5 text-nexus-text-tertiary"
            data-testid="back-to-customers-btn">
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>
              {isPersonal ? 'PERSONAL ACCOUNT' : 'ORGANIZATION'} · {t.industry || '—'}
            </div>
            <h1 className="font-heading text-3xl font-semibold text-nexus-text-primary mt-1 inline-flex items-center gap-2">
              {isPersonal ? <User size={22} /> : <Building2 size={22} />} {t.name}
            </h1>
            <p className="text-[12px] text-nexus-text-tertiary mt-1 inline-flex items-center gap-3">
              <span>{t.billing_email}</span>
              <span>·</span>
              <span>slug: <code>{t.slug}</code></span>
              {geo.country && (
                <>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1"><MapPin size={11}/>{geo.city ? `${geo.city}, ` : ''}{geo.country}</span>
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Tab strip */}
      <div className="flex gap-1 p-1 rounded-lg w-fit" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
        {TABS.map(k => (
          <button key={k} onClick={() => setTab(k)}
            className="px-3 h-8 rounded-md text-[11px] capitalize transition-all"
            style={{
              background: tab === k ? 'var(--layer-0)' : 'transparent',
              color: tab === k ? 'var(--nexus-text-primary)' : 'var(--nexus-text-tertiary)',
              fontWeight: tab === k ? 600 : 400,
            }}
            data-testid={`tab-${k}`}>{k}</button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === 'overview' && (
        <div className="space-y-4" data-testid="overview-pane">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label="Wallet"   value={`${(pool.credit_balance || 0).toFixed(1)}`}
                  sub={`${fmtNum(pool.lifetime_credits_purchased || 0)} credits purchased`} icon={Wallet} color="#10B981" />
            <Tile label="Consumed (90d)" value={`${totalCredits.toFixed(1)}`} sub={`${fmtNum(totalCalls)} calls · credits`} icon={Activity} color="#F26B1F" />
            <Tile label="Tokens (90d)"   value={fmtNum(totalTokens)} sub="input + output" icon={MessageSquare} color="#6BA6F5" />
            <Tile
              label="Revenue"
              value={revInr ? fmtMoney(revInr.total, 'INR') : '₹0'}
              sub={revUsd ? `+ ${fmtMoney(revUsd.total, 'USD')}` : `${data.invoices.length} invoices`}
              icon={Receipt} color="#A78BFA"
            />
          </div>

          {/* Usage timeseries */}
          <div className="rounded-xl p-4" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
            <div className="text-[10px] font-semibold tracking-wider uppercase mb-2" style={{ color: '#F26B1F' }}>USAGE · LAST 90 DAYS</div>
            {(data.timeseries || []).length < 2 ? (
              <div className="h-[240px] flex flex-col items-center justify-center text-center px-6"
                   data-testid="usage-empty-state">
                <Activity size={26} className="text-nexus-text-tertiary mb-2" />
                <p className="text-[13px] text-nexus-text-secondary">Not enough data yet</p>
                <p className="text-[11px] text-nexus-text-tertiary mt-1">
                  {(data.timeseries || []).length === 0
                    ? 'No consumption recorded for this tenant in the last 90 days.'
                    : `Only ${(data.timeseries || []).length} day of activity so far — chart appears once we have at least 2 days.`}
                </p>
              </div>
            ) : (
            <div style={{ height: 240 }}>
              <ResponsiveContainer>
                <AreaChart data={data.timeseries || []} margin={{ top: 8, right: 16, left: 16, bottom: 0 }}>
                  <defs>
                    <linearGradient id="cInGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1E4E8C" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#1E4E8C" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="cOutGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#F26B1F" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#F26B1F" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#A0AEC0' }} axisLine={false} tickLine={false} />
                  <YAxis width={56} tick={{ fontSize: 10, fill: '#A0AEC0' }} axisLine={false} tickLine={false}
                         tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v} />
                  <Tooltip contentStyle={{ background: '#0A1B2E', border: '1px solid #F26B1F', borderRadius: 8, fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="tokens_input"  stroke="#1E4E8C" fill="url(#cInGrad)"  strokeWidth={2} name="Input tokens" />
                  <Area type="monotone" dataKey="tokens_output" stroke="#F26B1F" fill="url(#cOutGrad)" strokeWidth={2} name="Output tokens" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            )}
          </div>

          {/* Model split */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,1fr] gap-3">
            <div className="rounded-xl p-4" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
              <div className="text-[10px] font-semibold tracking-wider uppercase mb-2" style={{ color: '#F26B1F' }}>BY MODEL</div>
              <div style={{ height: 200 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={data.by_model || []} dataKey="credits" nameKey="_id"
                         cx="50%" cy="50%" innerRadius={48} outerRadius={84} paddingAngle={2}>
                      {(data.by_model || []).map((e, i) =>
                        <Cell key={i} fill={MODEL_COLORS[e._id] || '#718096'} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#0A1B2E', border: '1px solid #F26B1F', borderRadius: 8, fontSize: 11 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-xl p-4" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
              <div className="text-[10px] font-semibold tracking-wider uppercase mb-2" style={{ color: '#F26B1F' }}>SIGNUP DETAILS</div>
              <ul className="space-y-2 text-[12px] text-nexus-text-secondary">
                <li className="flex justify-between"><span><Calendar size={11} className="inline mr-1 text-nexus-text-tertiary"/>Joined</span><span>{fmtDateTime(t.created_at)}</span></li>
                <li className="flex justify-between"><span><Globe2 size={11} className="inline mr-1 text-nexus-text-tertiary"/>Country</span><span>{geo.country || '—'} {geo.country_code && `(${geo.country_code})`}</span></li>
                <li className="flex justify-between"><span>City</span><span>{geo.city || '—'}{geo.region ? `, ${geo.region}` : ''}</span></li>
                <li className="flex justify-between"><span>Signup IP</span><span className="font-mono">{geo.ip || '—'}</span></li>
                <li className="flex justify-between"><span>ISP</span><span className="text-right max-w-[260px] truncate" title={geo.org}>{geo.org || '—'}</span></li>
                <li className="flex justify-between"><span>Members</span><span>{data.users.length} / {t.max_users || 999}</span></li>
                <li className="flex justify-between"><span>Status</span>
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{
                    background: t.is_active ? 'rgba(16,185,129,0.15)' : 'rgba(242,107,31,0.15)',
                    color: t.is_active ? '#10B981' : '#F26B1F',
                  }}>{t.is_active ? 'ACTIVE' : 'SUSPENDED'}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* CONVERSATIONS */}
      {tab === 'conversations' && (
        <div className="space-y-3" data-testid="conversations-pane">
          <div className="rounded-xl overflow-hidden" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
            <table className="w-full text-[12px]">
              <thead style={{ color: '#718096' }}>
                <tr>
                  <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Title</th>
                  <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">User</th>
                  <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Model</th>
                  <th className="text-right px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Tokens</th>
                  <th className="text-right px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Msgs</th>
                  <th className="text-center px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Feedback</th>
                  <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Updated</th>
                </tr>
              </thead>
              <tbody>
                {conversations.length === 0 && (
                  <tr><td colSpan="7" className="text-center py-10 text-nexus-text-tertiary">No conversations yet.</td></tr>
                )}
                {conversations.map((c) => (
                  <tr key={c.id}
                      className="border-t cursor-pointer hover:bg-white/[0.02]"
                      style={{ borderColor: 'var(--hairline)' }}
                      onClick={() => openConv(c)}
                      data-testid={`conv-row-${c.id}`}>
                    <td className="px-4 py-3 text-nexus-text-primary truncate max-w-[320px]">{c.title || '(untitled)'}</td>
                    <td className="px-4 py-3 text-nexus-text-secondary text-[11px]">{c.user?.email || '—'}</td>
                    <td className="px-4 py-3 text-[11px]"><span style={{ color: MODEL_COLORS[c.model_used] || '#718096' }}>{c.model_used || '—'}</span></td>
                    <td className="px-4 py-3 text-right font-mono">{fmtNum((c.total_input_tokens || 0) + (c.total_output_tokens || 0))}</td>
                    <td className="px-4 py-3 text-right">{c.message_count}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-2 text-[11px]">
                        {c.feedback_summary?.up   ? <span className="text-emerald-400 inline-flex items-center gap-0.5"><ThumbsUp size={10}/>{c.feedback_summary.up}</span> : null}
                        {c.feedback_summary?.down ? <span className="text-red-400 inline-flex items-center gap-0.5"><ThumbsDown size={10}/>{c.feedback_summary.down}</span> : null}
                        {!c.feedback_summary?.up && !c.feedback_summary?.down ? <span className="text-nexus-text-tertiary">—</span> : null}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-nexus-text-tertiary text-[11px]">{fmtDateTime(c.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Conversation viewer modal */}
          {activeConv && (
            <div className="fixed inset-0 z-50 flex items-stretch justify-end" onClick={() => setActiveConv(null)} data-testid="conv-viewer">
              <div className="absolute inset-0 bg-black/60" />
              <motion.div
                initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 28, stiffness: 220 }}
                className="relative w-full max-w-3xl h-full overflow-y-auto p-6"
                style={{ background: 'var(--layer-0)', borderLeft: '1px solid var(--hairline)' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>CONVERSATION</div>
                    <h2 className="font-heading text-xl text-nexus-text-primary mt-0.5">{activeConv.title || '(untitled)'}</h2>
                    <p className="text-[11px] text-nexus-text-tertiary mt-1">
                      {activeConv.user?.email} · {activeConv.model_used} · {fmtDateTime(activeConv.created_at)}
                    </p>
                  </div>
                  <button onClick={() => setActiveConv(null)}
                    className="px-2 py-1 rounded-md text-[11px] text-nexus-text-tertiary hover:text-nexus-text-primary hover:bg-white/5"
                    data-testid="close-conv-btn">Close</button>
                </div>
                <div className="space-y-3">
                  {(activeConv.messages || []).map((m, i) => {
                    const fb = (activeConv.feedback || []).find(f => f.message_index === i);
                    return (
                      <div key={i} className="rounded-lg p-3"
                        style={{ background: m.role === 'user' ? 'rgba(110,166,245,0.06)' : 'var(--layer-1)',
                                 border: '1px solid var(--hairline)' }}
                        data-testid={`msg-${i}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[10px] uppercase tracking-wider font-semibold"
                                style={{ color: m.role === 'user' ? '#6BA6F5' : '#F26B1F' }}>
                            {m.role}
                          </span>
                          {fb && (
                            <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${
                              fb.vote === 'up' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'
                            }`}>
                              {fb.vote === 'up' ? <ThumbsUp size={10}/> : <ThumbsDown size={10}/>}
                              {fb.comment ? ` "${fb.comment.slice(0, 50)}"` : fb.vote}
                            </span>
                          )}
                        </div>
                        <pre className="text-[12px] text-nexus-text-secondary whitespace-pre-wrap leading-relaxed font-sans">{m.content || ''}</pre>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            </div>
          )}
        </div>
      )}

      {/* INVOICES */}
      {tab === 'invoices' && (
        <div className="rounded-xl overflow-hidden" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }} data-testid="invoices-pane">
          <table className="w-full text-[12px]">
            <thead style={{ color: '#718096' }}>
              <tr>
                <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Invoice #</th>
                <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Issued</th>
                <th className="text-right px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Credits</th>
                <th className="text-right px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Subtotal</th>
                <th className="text-right px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">GST</th>
                <th className="text-right px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Total</th>
                <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Status</th>
                <th className="px-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.invoices.length === 0 && (
                <tr><td colSpan="8" className="text-center py-10 text-nexus-text-tertiary">No invoices yet.</td></tr>
              )}
              {data.invoices.map((inv) => (
                <tr key={inv.id} className="border-t" style={{ borderColor: 'var(--hairline)' }} data-testid={`inv-row-${inv.invoice_number}`}>
                  <td className="px-4 py-3 font-mono text-nexus-text-primary">{inv.invoice_number}</td>
                  <td className="px-4 py-3 text-nexus-text-tertiary">{fmtDateTime(inv.created_at)}</td>
                  <td className="px-4 py-3 text-right">{fmtNum(inv.credits_purchased)}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmtMoney(inv.subtotal, inv.currency)}</td>
                  <td className="px-4 py-3 text-right font-mono">{inv.gst_amount ? fmtMoney(inv.gst_amount, inv.currency) : '—'}</td>
                  <td className="px-4 py-3 text-right font-mono text-nexus-text-primary">{fmtMoney(inv.total, inv.currency)}</td>
                  <td className="px-4 py-3">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase"
                      style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981' }}>
                      {inv.status || 'paid'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => downloadInvoice(inv)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold text-white"
                      style={{ background: '#F26B1F' }}
                      data-testid={`download-pdf-${inv.invoice_number}`}>
                      <Download size={11} /> PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* MEMBERS */}
      {tab === 'members' && (
        <div className="rounded-xl overflow-hidden" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }} data-testid="members-pane">
          <table className="w-full text-[12px]">
            <thead style={{ color: '#718096' }}>
              <tr>
                <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Email</th>
                <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Name</th>
                <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Role</th>
                <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Provider</th>
                <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Joined</th>
                <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Last active</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map(u => (
                <tr key={u.id} className="border-t" style={{ borderColor: 'var(--hairline)' }} data-testid={`member-row-${u.email}`}>
                  <td className="px-4 py-3 text-nexus-text-primary">{u.email}</td>
                  <td className="px-4 py-3 text-nexus-text-secondary">{u.name || '—'}</td>
                  <td className="px-4 py-3 text-[11px] uppercase tracking-wider">{u.role}</td>
                  <td className="px-4 py-3 text-nexus-text-tertiary">{u.auth_provider || 'local'}</td>
                  <td className="px-4 py-3 text-nexus-text-tertiary">{fmtDateTime(u.created_at)}</td>
                  <td className="px-4 py-3 text-nexus-text-tertiary">{fmtDateTime(u.last_active_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* LEDGER */}
      {tab === 'ledger' && (
        <div className="rounded-xl overflow-hidden" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }} data-testid="ledger-pane">
          <table className="w-full text-[12px]">
            <thead style={{ color: '#718096' }}>
              <tr>
                <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Date</th>
                <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Type</th>
                <th className="text-right px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Credits</th>
                <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Model</th>
                <th className="text-right px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Tokens</th>
                <th className="text-right px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Balance</th>
              </tr>
            </thead>
            <tbody>
              {data.ledger.length === 0 && (
                <tr><td colSpan="6" className="text-center py-10 text-nexus-text-tertiary">No ledger entries yet.</td></tr>
              )}
              {data.ledger.map((l, i) => (
                <tr key={l.id || i} className="border-t" style={{ borderColor: 'var(--hairline)' }}>
                  <td className="px-4 py-3 text-nexus-text-tertiary">{(l.created_at || '').slice(0,16).replace('T',' ')}</td>
                  <td className="px-4 py-3 capitalize">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                      style={{
                        background: l.credits > 0 ? 'rgba(110,231,183,0.12)' : 'rgba(242,107,31,0.12)',
                        color:      l.credits > 0 ? '#10B981' : '#F26B1F',
                      }}>{l.type}</span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono"
                      style={{ color: l.credits > 0 ? '#10B981' : '#F26B1F' }}>
                    {l.credits > 0 ? '+' : ''}{Number(l.credits).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-nexus-text-tertiary text-[11px]">{l.model || '—'}</td>
                  <td className="px-4 py-3 text-right font-mono text-nexus-text-tertiary text-[11px]">
                    {l.tokens_total ? l.tokens_total.toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-nexus-text-primary">
                    {l.balance_after != null ? Number(l.balance_after).toFixed(1) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminCustomerDetail;
